<template>
  <div class="pricing-success-page">
    <div class="row justify-content-center">
      <div class="col-lg-8 col-md-10 col-sm-12">
        <div class="card">
          <div class="card-body text-center">
            <div v-if="loading">
              <h2 class="mb-2">Waiting for payment confirmation...</h2>
              <p class="text-muted mb-0">We are checking webhook status.</p>
            </div>

            <div v-else-if="errorMessage">
              <h2 class="mb-2">We could not load your purchase status</h2>
              <p class="text-muted mb-3">{{ errorMessage }}</p>
              <div class="mt-3">
                <button class="btn btn-primary btn-round mr-2" @click="goToBilling">Back to Billing</button>
                <button class="btn btn-outline-primary btn-round" @click="retry">Try Again</button>
              </div>
            </div>

            <div v-else-if="status === 'pending'">
              <h2 class="mb-2">Purchase pending</h2>
              <p class="text-muted mb-2">
                Reference: <b>{{ referenceId }}</b>
              </p>
              <p v-if="providerOrderId" class="text-muted mb-2">
                Order: <b>{{ providerOrderId }}</b>
              </p>
              <p class="text-muted mb-3">
                Payment is pending webhook confirmation. This page auto-refreshes.
              </p>
              <div class="mt-3">
                <button class="btn btn-primary btn-round mr-2" @click="retry">Refresh status</button>
                <button class="btn btn-outline-primary btn-round" @click="goToBilling">Back to Billing</button>
              </div>
            </div>

            <div v-else-if="status === 'failed'">
              <h2 class="mb-2">Purchase failed</h2>
              <p class="text-muted mb-1">Reference: <b>{{ referenceId }}</b></p>
              <p v-if="providerOrderId" class="text-muted mb-1">Order: <b>{{ providerOrderId }}</b></p>
              <p class="text-muted mb-3">{{ purchaseErrorMessage || "We received a failed status for this purchase." }}</p>
              <div class="mt-3">
                <button class="btn btn-primary btn-round mr-2" @click="goToBilling">Back to Billing</button>
                <button class="btn btn-outline-primary btn-round" @click="retry">Check again</button>
              </div>
            </div>

            <div v-else>
              <h2 class="mb-2">Purchase confirmed</h2>
              <p class="text-muted mb-1">Reference: <b>{{ referenceId }}</b></p>
              <p v-if="providerOrderId" class="text-muted mb-1">Order: <b>{{ providerOrderId }}</b></p>
              <p class="text-muted mb-1">Pack: <b>{{ purchasePackLabel }}</b></p>
              <p class="text-muted mb-1">Tokens added: <b>{{ purchaseTokensAdded }}</b></p>
              <p class="text-muted mb-3">New balance: <b>{{ billingStatus?.credits_remaining ?? "--" }}</b></p>
              <div class="mt-3">
                <button class="btn btn-primary btn-round mr-2" @click="goToCreateClips">Continue</button>
                <button class="btn btn-outline-primary btn-round" @click="goToBilling">View Billing</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { supabase } from "@/supabase";
import { useCurrentUserStore } from "@/stores/currentUser";
import { buildAuthHeaders } from "@/utils/impersonation";

const PENDING_PURCHASE_STORAGE_KEY = "clipr_ecartpay_pending_purchase";
const POLL_INTERVAL_MS = 4000;

export default {
  name: "BillingSuccess",
  data() {
    return {
      loading: true,
      errorMessage: null,
      referenceId: null,
      status: "pending",
      billingStatus: null,
      purchaseStatusPayload: null,
      poller: null,
    };
  },
  async mounted() {
    this.billingStatus = useCurrentUserStore().billingStatus;
    await this.fetchStatus();
    this.startPollingIfPending();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    getApiUrl() {
      return process.env.VUE_APP_API_URL || "http://localhost:8000";
    },
    goToCreateClips() {
      this.$router.push("/create-clips");
    },
    goToBilling() {
      this.$router.push("/billing");
    },
    resolveReferenceId() {
      const fromQuery = this.$route?.query?.reference_id;
      if (fromQuery && typeof fromQuery === "string") return fromQuery;
      try {
        const raw = localStorage.getItem(PENDING_PURCHASE_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed?.reference_id || null;
      } catch (error) {
        return null;
      }
    },
    async getSessionOrFail() {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error || !session || !session.access_token) {
        throw new Error("You must be logged in to check purchase status.");
      }
      return session;
    },
    async retry() {
      await this.fetchStatus();
      this.startPollingIfPending();
    },
    stopPolling() {
      if (this.poller) {
        clearInterval(this.poller);
        this.poller = null;
      }
    },
    startPollingIfPending() {
      this.stopPolling();
      if (this.status !== "pending" || !this.referenceId) return;
      this.poller = setInterval(() => {
        this.fetchStatus();
      }, POLL_INTERVAL_MS);
    },
    persistPendingReference(statusValue) {
      if (!this.referenceId) return;
      if (statusValue === "confirmed") {
        localStorage.removeItem(PENDING_PURCHASE_STORAGE_KEY);
        return;
      }
      localStorage.setItem(
        PENDING_PURCHASE_STORAGE_KEY,
        JSON.stringify({
          reference_id: this.referenceId,
          status: statusValue || "pending",
        })
      );
    },
    async fetchStatus() {
      this.loading = true;
      this.errorMessage = null;
      try {
        this.referenceId = this.resolveReferenceId();
        if (!this.referenceId) {
          this.status = "pending";
          this.errorMessage = "No reference_id found for this purchase.";
          return;
        }

        const session = await this.getSessionOrFail();
        const response = await fetch(
          `${this.getApiUrl()}/momo/ecartpay/purchase-status?reference_id=${encodeURIComponent(this.referenceId)}`,
          {
            method: "GET",
            headers: buildAuthHeaders(session, { includeContentType: false }),
          }
        );
        if (!response.ok) {
          const message = await response.text();
          throw new Error(message || "Failed to retrieve purchase status.");
        }
        const payload = await response.json();
        this.purchaseStatusPayload = payload;
        this.status = payload?.status || "pending";
        this.persistPendingReference(this.status);
        if (this.status === "confirmed") {
          if (payload?.billing_status) {
            this.billingStatus = payload.billing_status;
            useCurrentUserStore().applyBillingStatusSnapshot(payload.billing_status);
          } else {
            this.billingStatus = useCurrentUserStore().billingStatus;
          }
          this.stopPolling();
        } else if (this.status === "failed") {
          this.stopPolling();
        }
      } catch (error) {
        this.errorMessage = error?.message || "Unexpected error while checking purchase status.";
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    providerOrderId() {
      const raw = this.purchaseStatusPayload?.provider_order_id;
      if (!raw) return "";
      return raw.toString();
    },
    purchaseTokensAdded() {
      const raw = this.purchaseStatusPayload?.tokens;
      if (raw === null || raw === undefined || Number.isNaN(Number(raw))) return "--";
      return Number(raw).toLocaleString();
    },
    purchasePackLabel() {
      const code = (this.purchaseStatusPayload?.pack_code || "").toString();
      return code || "--";
    },
    purchaseErrorMessage() {
      return this.purchaseStatusPayload?.error_message || "";
    },
  },
};
</script>

<style scoped lang="scss">
.pricing-success-page {
  padding: 20px 0;
}
</style>
